/**
 * Serialization layer for the x-allOf rule builder.
 *
 * Rule (UI state):
 *   { id, negated, ifConditions[], thenFields[], elseFields[], raw? }
 *
 * IfCondition:
 *   { field, operator, value?, required }
 *   `field` is a display path (e.g. 'address.country', 'manifest[].consignee.email').
 *
 * FieldConstraint:
 *   { fieldName, required, type, arrayItemType?, minLength?, maxLength?,
 *     minimum?, maximum?, exclusiveMinimum?, exclusiveMaximum?, pattern?,
 *     format?, enumValues?, containsConst?, nullableInBranch?, raw? }
 *   `fieldName` is a display path too.
 *
 * Schema (persisted JSON, draft 2020-12):
 *   [{ if: {...}, then: {...}, else?: {...} }, ...]
 *
 * The parser is tolerant — unknown keywords land in a `raw` escape hatch so
 * byte-identical round-trips survive even when a human hand-edits the JSON.
 * The emitter merges `raw` last so preserved keywords always win.
 *
 * Phase 1b: paths.js owns path <-> JSON Schema wrapper translation. This file
 * emits/parses leaf operator snippets and delegates nesting to
 * emitPathCondition / parsePathFromProperties.
 */

import {
	displayToPath,
	pathToDisplay,
	emitPathCondition,
	mergePathFragments,
	parsePathFromProperties,
	collectRequiredByPath,
} from './paths';

export const NUMERIC_OPERATORS = new Set([
	'minimum', 'maximum', 'exclusive_minimum', 'exclusive_maximum',
]);

// ============ ID helpers ============

/** djb2 — tiny stable hash for rule content addressing. */
export function djb2Hash(str) {
	let hash = 5381;
	for (let i = 0; i < str.length; i += 1) {
		hash = (hash * 33) ^ str.charCodeAt(i);
	}
	return (hash >>> 0).toString(16).padStart(8, '0');
}

/** Canonical JSON — recursive alpha-sort keys so hashes are order-independent. */
export function canonicalize(value) {
	if (Array.isArray(value)) return value.map(canonicalize);
	if (value && typeof value === 'object') {
		return Object.keys(value).sort().reduce((acc, k) => {
			acc[k] = canonicalize(value[k]);
			return acc;
		}, {});
	}
	return value;
}

export function newRuleId() {
	return `rule-${crypto.randomUUID()}`;
}

// ============ Deep merge (for `raw` escape hatch) ============

function isPlainObject(v) {
	return v !== null && typeof v === 'object' && !Array.isArray(v);
}

export function deepMerge(target, source) {
	if (!isPlainObject(source)) return source === undefined ? target : source;
	const base = isPlainObject(target) ? { ...target } : {};
	for (const [k, v] of Object.entries(source)) {
		base[k] = isPlainObject(v) && isPlainObject(base[k]) ? deepMerge(base[k], v) : v;
	}
	return base;
}

// ============ Value coercion ============

function coerceScalar(raw) {
	if (raw === null || raw === undefined) return raw;
	const s = String(raw).trim();
	if (s === '') return '';
	if (s === 'true') return true;
	if (s === 'false') return false;
	if (s === 'null') return null;
	const n = Number(s);
	return Number.isNaN(n) ? s : n;
}

function coerceList(raw) {
	if (Array.isArray(raw)) return raw.map(coerceScalar);
	return String(raw ?? '')
		.split(',')
		.map((v) => v.trim())
		.filter((v) => v !== '')
		.map(coerceScalar);
}

// ============ Emitter — rules -> x-allOf JSON ============

function emitIfCondition(cond) {
	switch (cond.operator) {
		case 'const':
			return { const: coerceScalar(cond.value) };
		case 'enum':
			return { enum: coerceList(cond.value) };
		case 'not_in':
			return { not: { enum: coerceList(cond.value) } };
		case 'minimum':
			return { minimum: Number(cond.value) };
		case 'maximum':
			return { maximum: Number(cond.value) };
		case 'exclusive_minimum':
			return { exclusiveMinimum: Number(cond.value) };
		case 'exclusive_maximum':
			return { exclusiveMaximum: Number(cond.value) };
		case 'contains_const':
			return { contains: { const: coerceScalar(cond.value) } };
		case 'type_is_null':
			return { type: 'null' };
		default:
			return null;
	}
}

// JSON Schema draft-2020-12 doesn't accept `date` / `date-time` as types —
// lower them to `{ type: 'string', format: '<date|date-time>' }` on emit.
function lowerDateLikeType(type) {
	if (type === 'date') return { type: 'string', format: 'date' };
	if (type === 'date-time') return { type: 'string', format: 'date-time' };
	return null;
}

function emitFieldConstraint(field) {
	const out = {};
	// A field parsed from an uploaded schema that had no explicit `type`
	// leaves `field.type == null`. Treat that as "don't emit type" so a
	// `{ const: 0 }` on an integer root doesn't get coerced to `string`.
	const hasExplicitType = field.type != null && field.type !== '';
	const baseType = field.type;
	const lowered = hasExplicitType ? lowerDateLikeType(baseType) : null;

	if (field.type === 'array') {
		out.type = 'array';
		if (field.arrayItemType) {
			const loweredItem = lowerDateLikeType(field.arrayItemType);
			out.items = loweredItem ? loweredItem : { type: field.arrayItemType };
		}
	} else if (lowered) {
		if (field.nullableInBranch) {
			out.type = [lowered.type, 'null'];
		} else {
			out.type = lowered.type;
		}
		out.format = lowered.format;
	} else if (hasExplicitType && field.nullableInBranch) {
		out.type = [baseType, 'null'];
	} else if (hasExplicitType) {
		out.type = baseType;
	}

	if (field.minLength != null && field.minLength !== '') {
		out.minLength = Number.parseInt(field.minLength, 10);
	}
	if (field.maxLength != null && field.maxLength !== '') {
		out.maxLength = Number.parseInt(field.maxLength, 10);
	}
	if (field.minimum != null && field.minimum !== '') {
		out.minimum = parseFloat(field.minimum);
	}
	if (field.maximum != null && field.maximum !== '') {
		out.maximum = parseFloat(field.maximum);
	}
	if (field.exclusiveMinimum != null && field.exclusiveMinimum !== '') {
		out.exclusiveMinimum = parseFloat(field.exclusiveMinimum);
	}
	if (field.exclusiveMaximum != null && field.exclusiveMaximum !== '') {
		out.exclusiveMaximum = parseFloat(field.exclusiveMaximum);
	}
	if (field.pattern) out.pattern = field.pattern;
	if (field.format) out.format = field.format;
	if (Array.isArray(field.enumValues) && field.enumValues.length > 0) {
		out.enum = field.enumValues;
	} else if (typeof field.enumValues === 'string' && field.enumValues.trim() !== '') {
		out.enum = coerceList(field.enumValues);
	}
	if (field.containsConst != null && field.containsConst !== '') {
		out.contains = { const: coerceScalar(field.containsConst) };
	}

	if (isPlainObject(field.raw)) return deepMerge(out, field.raw);
	return out;
}

// If `containerRequired` is an array, convert it to a Set so the emitter
// only marks listed containers as required. If it is `undefined` (pre-fix
// rules, or rules authored fresh in the UI), return `undefined` so the
// emitter falls back to the legacy "always require containers" behaviour.
function containerSetFor(field) {
	if (!Array.isArray(field?.containerRequired)) return undefined;
	return new Set(field.containerRequired);
}

function emitBranch(fields) {
	let acc = {};
	for (const f of fields) {
		const path = displayToPath(f.fieldName);
		if (path.length === 0) continue;
		const leaf = emitFieldConstraint(f);
		const wrapped = emitPathCondition(path, leaf, {
			requireLeaf: f.required !== false,
			requiredContainerDisplayPaths: containerSetFor(f),
		});
		acc = mergePathFragments(acc, wrapped);
	}
	return acc;
}

function emitIfBody(ifConditions) {
	let acc = {};
	for (const c of ifConditions) {
		const snippet = emitIfCondition(c);
		if (snippet === null) continue;
		const path = displayToPath(c.field);
		if (path.length === 0) continue;
		const wrapped = emitPathCondition(path, snippet, {
			requireLeaf: c.required !== false,
			requiredContainerDisplayPaths: containerSetFor(c),
		});
		acc = mergePathFragments(acc, wrapped);
	}
	return acc;
}

/**
 * Emit a single rule. Returns `null` when the rule carries nothing to emit —
 * callers filter these out so the schema never contains a vacuously-true
 * `{ if: { properties: {} } }` entry (which would corrupt every payload).
 * A rule is emittable when EITHER the UI has authored ifConditions that
 * produce a non-empty body, OR the uploaded rule's `raw.if` preserves an
 * unrecognized operator (e.g. `pattern` in IF) via the escape hatch.
 */
export function emitRule(rule) {
	if (!rule) return null;

	const hasConditions = Array.isArray(rule.ifConditions) && rule.ifConditions.length > 0;
	const hasRawIf = isPlainObject(rule.raw?.if);
	if (!hasConditions && !hasRawIf) return null;

	const body = hasConditions ? emitIfBody(rule.ifConditions) : {};
	const bodyEmpty = !isPlainObject(body.properties) || Object.keys(body.properties).length === 0;
	if (hasConditions && bodyEmpty && !hasRawIf) return null;

	const out = {
		if: rule.negated ? { not: body } : body,
		then: emitBranch(rule.thenFields || []),
	};
	if (Array.isArray(rule.elseFields) && rule.elseFields.length > 0) {
		out.else = emitBranch(rule.elseFields);
	}

	if (isPlainObject(rule.raw)) {
		if (rule.raw.if) out.if = deepMerge(out.if, rule.raw.if);
		if (rule.raw.then) out.then = deepMerge(out.then, rule.raw.then);
		if (rule.raw.else) out.else = deepMerge(out.else ?? {}, rule.raw.else);
	}

	// Final guard: after deep-merge with raw.if, the IF body must have at
	// least one property or negated.not.properties entry. A totally empty
	// IF would match every payload.
	const ifCheck = rule.negated ? out.if?.not : out.if;
	const finalIfEmpty = !isPlainObject(ifCheck?.properties)
		|| Object.keys(ifCheck.properties).length === 0;
	if (finalIfEmpty) return null;

	return out;
}

export function rulesToXAllOf(rules) {
	if (!Array.isArray(rules) || rules.length === 0) return null;
	const emitted = rules.map(emitRule).filter((r) => r !== null);
	return emitted.length > 0 ? emitted : null;
}

// ============ Parser — x-allOf JSON -> rules ============

const KNOWN_OP_KEYWORDS = new Set([
	'const', 'enum', 'not',
	'minimum', 'maximum', 'exclusiveMinimum', 'exclusiveMaximum',
	'contains', 'type',
]);

function detectOperator(schema) {
	if (!isPlainObject(schema)) return null;
	if ('const' in schema) {
		return { operator: 'const', value: schema.const };
	}
	if (Array.isArray(schema.enum)) {
		return { operator: 'enum', value: schema.enum };
	}
	if (schema.not && Array.isArray(schema.not.enum)
		&& Object.keys(schema.not).length === 1) {
		return { operator: 'not_in', value: schema.not.enum };
	}
	if ('exclusiveMinimum' in schema) {
		return { operator: 'exclusive_minimum', value: schema.exclusiveMinimum };
	}
	if ('exclusiveMaximum' in schema) {
		return { operator: 'exclusive_maximum', value: schema.exclusiveMaximum };
	}
	if ('minimum' in schema) {
		return { operator: 'minimum', value: schema.minimum };
	}
	if ('maximum' in schema) {
		return { operator: 'maximum', value: schema.maximum };
	}
	if (schema.contains && isPlainObject(schema.contains) && 'const' in schema.contains) {
		return { operator: 'contains_const', value: schema.contains.const };
	}
	if (schema.type === 'null'
		|| (Array.isArray(schema.type) && schema.type.length === 1 && schema.type[0] === 'null')) {
		return { operator: 'type_is_null' };
	}
	return null;
}

function extractUnknownKeywords(schema) {
	if (!isPlainObject(schema)) return null;
	const unknown = {};
	for (const [k, v] of Object.entries(schema)) {
		if (!KNOWN_OP_KEYWORDS.has(k)) unknown[k] = v;
	}
	return Object.keys(unknown).length > 0 ? unknown : null;
}

// Collect the display paths of intermediate PROPERTY segments along `path`
// that the uploaded schema marked required on their parent. Emitters use
// this to decide whether to emit `required: [name]` for each container on
// round-trip, instead of unconditionally adding one at every level (GAP-7).
function collectRequiredContainersForLeaf(path, requiredByPath) {
	if (!Array.isArray(path) || path.length <= 1) return [];
	const out = [];
	for (let i = 0; i < path.length - 1; i += 1) {
		if (path[i].kind !== 'property') continue;
		const display = pathToDisplay(path.slice(0, i + 1));
		if (requiredByPath.get(display) === true) out.push(display);
	}
	return out;
}

function parseIfBody(body) {
	const conds = [];
	const rawProps = {};
	if (!isPlainObject(body)) return { conds, rawProps };

	const requiredByPath = collectRequiredByPath(body);
	const leaves = parsePathFromProperties(body);

	for (const { path, leaf } of leaves) {
		const display = pathToDisplay(path);
		const detected = detectOperator(leaf);
		const unknown = extractUnknownKeywords(leaf);
		if (detected) {
			conds.push({
				field: display,
				operator: detected.operator,
				value: detected.value,
				required: requiredByPath.get(display) === true,
				containerRequired: collectRequiredContainersForLeaf(path, requiredByPath),
			});
			if (unknown) rawProps[display] = unknown;
		} else {
			rawProps[display] = leaf;
		}
	}
	return { conds, rawProps };
}

function parseFieldConstraint(name, prop, required) {
	if (!isPlainObject(prop)) {
		// Mirror the main path: leave `type` unset when the uploaded schema
		// has no object with an explicit `type` keyword. Injecting 'string'
		// here would corrupt boolean/`$ref` subschemas on round-trip.
		return { fieldName: name, required };
	}
	const rawType = prop.type;
	// Leave `type` unset when the uploaded property has no explicit `type`
	// keyword so the emitter can omit it on round-trip. Previously defaulted
	// to `'string'`, which silently changed a `const: 0` on an integer root
	// into `{ const: 0, type: 'string' }`.
	let type = null;
	let nullableInBranch = false;
	let explicitType = false;
	if (Array.isArray(rawType)) {
		const nonNull = rawType.find((t) => t !== 'null');
		type = nonNull || 'string';
		nullableInBranch = rawType.includes('null');
		explicitType = true;
	} else if (rawType) {
		type = rawType;
		explicitType = true;
	}

	const field = {
		fieldName: name,
		required,
	};
	if (explicitType) field.type = type;
	if (nullableInBranch) field.nullableInBranch = true;
	if (type === 'array' && isPlainObject(prop.items) && typeof prop.items.type === 'string') {
		field.arrayItemType = prop.items.type;
	}
	if (prop.minLength != null) field.minLength = prop.minLength;
	if (prop.maxLength != null) field.maxLength = prop.maxLength;
	if (prop.minimum != null) field.minimum = prop.minimum;
	if (prop.maximum != null) field.maximum = prop.maximum;
	if (prop.exclusiveMinimum != null) field.exclusiveMinimum = prop.exclusiveMinimum;
	if (prop.exclusiveMaximum != null) field.exclusiveMaximum = prop.exclusiveMaximum;
	if (prop.pattern) field.pattern = prop.pattern;
	if (prop.format) field.format = prop.format;
	if (Array.isArray(prop.enum)) field.enumValues = prop.enum;
	if (isPlainObject(prop.contains) && 'const' in prop.contains) {
		field.containsConst = prop.contains.const;
	}

	const KNOWN_FIELD_KEYWORDS = new Set([
		'type', 'items', 'minLength', 'maxLength',
		'minimum', 'maximum', 'exclusiveMinimum', 'exclusiveMaximum',
		'pattern', 'format', 'enum', 'contains',
	]);
	const raw = {};
	for (const [k, v] of Object.entries(prop)) {
		if (!KNOWN_FIELD_KEYWORDS.has(k)) raw[k] = v;
	}
	if (Object.keys(raw).length > 0) field.raw = raw;
	return field;
}

function parseBranch(branch) {
	if (!isPlainObject(branch)) return [];
	const leaves = parsePathFromProperties(branch);
	const requiredByPath = collectRequiredByPath(branch);
	return leaves.map(({ path, leaf }) => {
		const display = pathToDisplay(path);
		const field = parseFieldConstraint(display, leaf, requiredByPath.get(display) === true);
		field.containerRequired = collectRequiredContainersForLeaf(path, requiredByPath);
		return field;
	});
}

function parseRule(entry, index) {
	if (!isPlainObject(entry)) return null;
	const ifNode = entry.if || {};
	const negated = isPlainObject(ifNode.not);
	const body = negated ? ifNode.not : ifNode;
	const { conds, rawProps } = parseIfBody(body);

	const raw = {};
	const bodyRawExtras = {};
	if (isPlainObject(body)) {
		for (const [k, v] of Object.entries(body)) {
			if (k !== 'properties' && k !== 'required') bodyRawExtras[k] = v;
		}
	}
	if (Object.keys(rawProps).length > 0) {
		bodyRawExtras.properties = rawProps;
	}
	if (Object.keys(bodyRawExtras).length > 0) {
		raw.if = negated ? { not: bodyRawExtras } : bodyRawExtras;
	}

	// Identity must not change when only `then`/`else` are edited — otherwise a
	// modified rule round-trips through upload as removed+added in diffXAllOfRules.
	// Hash only the `if` + negation flag; equality of rule *content* is still
	// enforced via canonicalize(emitRule(rule)) in the diff itself.
	const identityFor = { if: entry.if ?? null, negated };
	const id = `rule-${index}-${djb2Hash(JSON.stringify(canonicalize(identityFor)))}`;

	return {
		id,
		negated,
		ifConditions: conds,
		thenFields: parseBranch(entry.then),
		elseFields: parseBranch(entry.else),
		...(Object.keys(raw).length > 0 ? { raw } : {}),
	};
}

export function xAllOfToRules(xAllOf) {
	if (!Array.isArray(xAllOf)) return [];
	return xAllOf
		.map((entry, i) => parseRule(entry, i))
		.filter((r) => r !== null);
}
